import { useCallback, useEffect, useState } from 'react';
import { LEAGUE, NOTIFY_EVENTS, type NotifyEventType, type NotifyPrefs } from '@ff/core';
import {
  fetchPushSettings,
  savePushPrefs,
  sendTestPush,
  type PushSettings,
} from '../lib/api.ts';
import {
  currentSubscription,
  disablePush,
  enablePush,
  isIos,
  isStandalone,
  permissionState,
  pushSupported,
  unregisterServiceWorker,
} from '../lib/push.ts';
import { useInstall } from '../lib/install.tsx';

const USE_MOCK = import.meta.env.VITE_USE_MOCK === '1';

/**
 * "Tell me when something happens." Most of this component is the branch before
 * the toggles: what a device is capable of varies enormously, and the honest
 * answer for an iPhone in a Safari tab is "install this first", not a button
 * that does nothing.
 */
export function NotificationSettings() {
  const { installed } = useInstall();
  const [settings, setSettings] = useState<PushSettings | null>(null);
  const [subscribed, setSubscribed] = useState(false);
  const [permission, setPermission] = useState(permissionState());
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    const [loaded, sub] = await Promise.all([
      fetchPushSettings().catch(() => null),
      currentSubscription().catch(() => null),
    ]);
    if (loaded) setSettings(loaded);
    setSubscribed(sub !== null);
    setPermission(permissionState());
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  // iOS can't do any of this from a browser tab. The install card above says how;
  // this only has to explain why it's a prerequisite rather than a suggestion.
  const iosNeedsInstall = isIos() && !isStandalone() && !installed;
  if (iosNeedsInstall) {
    return (
      <section className="space-y-3">
        <p className="eyebrow">Notifications</p>
        <div className="card p-6">
          <p className="text-sm text-chalk-dim">
            On an iPhone, notifications only work once {LEAGUE.bookName} is on your home
            screen — add it using the steps above, open it from the icon, and
            you'll be able to turn them on here.
          </p>
        </div>
      </section>
    );
  }

  if (!pushSupported()) {
    return (
      <section className="space-y-3">
        <p className="eyebrow">Notifications</p>
        <div className="card p-6">
          <p className="text-sm text-chalk-dim">
            {USE_MOCK
              ? 'Notifications need the real backend — they are off in this preview.'
              : `This browser can't do notifications. Chrome or Edge on a computer or Android will, and so will an iPhone once ${LEAGUE.bookName} is on the home screen.`}
          </p>
        </div>
      </section>
    );
  }

  async function toggle(id: NotifyEventType, on: boolean) {
    if (!settings) return;
    const next: NotifyPrefs = { ...settings.prefs, [id]: on };
    // Optimistic: a checkbox that waits on a round-trip feels broken.
    setSettings({ ...settings, prefs: next });
    try {
      await savePushPrefs(next);
    } catch (e) {
      setSettings(settings);
      setError(e instanceof Error ? e.message : 'Could not save that');
    }
  }

  async function turnOn() {
    setBusy(true);
    setError(null);
    setNote(null);
    // No await before this call: enablePush asks for permission first, because
    // Safari revokes the click's authority the moment anything is awaited.
    const result = await enablePush();
    setBusy(false);
    setPermission(permissionState());
    if (result.ok) {
      setNote('Notifications are on for this device.');
      void refresh();
      return;
    }
    setError(
      result.reason === 'denied'
        ? 'You said no to notifications. Turn them back on in your browser settings for this site.'
        : result.reason === 'not-configured'
          ? "The server doesn't have notification keys set up yet."
          : result.reason === 'unsupported'
            ? "This browser can't do notifications."
            : result.reason === 'no-worker'
              ? 'The app background worker never started — reload the page and try again.'
              : (result.detail ?? 'Could not turn notifications on.'),
    );
  }

  async function turnOff() {
    setBusy(true);
    await disablePush();
    setBusy(false);
    setNote('Off for this device. Your other devices are untouched.');
    void refresh();
  }

  async function test() {
    setBusy(true);
    setError(null);
    setNote(null);
    try {
      const sent = await sendTestPush();
      setNote(sent > 0 ? `Sent to ${sent} device${sent === 1 ? '' : 's'}.` : 'Nothing to send to.');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Test failed');
    } finally {
      setBusy(false);
    }
  }

  const serverOff = settings !== null && !settings.enabled;

  return (
    <section className="space-y-3">
      <p className="eyebrow">Notifications</p>
      <div className="card space-y-4 p-6">
        {serverOff && (
          <p className="font-mono text-xs text-blood">
            Notifications aren't configured on the server yet.
          </p>
        )}

        {permission === 'denied' ? (
          <p className="text-sm text-chalk-dim">
            This browser is blocking notifications for {LEAGUE.bookName}. The prompt won't
            come back on its own — turn them back on in your browser's settings
            for this site, then reload.
          </p>
        ) : !subscribed ? (
          <>
            <p className="text-sm text-chalk-dim">
              Get a nudge when somebody talks trash, when the roast drops, and
              when a week's money settles.
            </p>
            <button
              onClick={turnOn}
              disabled={busy || serverOff}
              className="rounded-md bg-brass px-3 py-1.5 font-display text-sm font-bold text-chalk hover:opacity-90 disabled:opacity-40"
            >
              {busy ? 'Turning on…' : 'Turn on notifications'}
            </button>
          </>
        ) : (
          <>
            <div className="space-y-3">
              {NOTIFY_EVENTS.map((event) => (
                <label key={event.id} className="flex cursor-pointer items-start gap-3">
                  <input
                    type="checkbox"
                    checked={settings?.prefs[event.id] ?? true}
                    onChange={(e) => void toggle(event.id, e.target.checked)}
                    className="mt-1 h-4 w-4 shrink-0 accent-brass"
                  />
                  <span>
                    <span className="block font-display text-sm font-bold">{event.label}</span>
                    <span className="block text-xs text-chalk-dim">{event.description}</span>
                  </span>
                </label>
              ))}
            </div>

            <div className="flex flex-wrap items-center gap-3 border-t border-felt-line pt-4">
              <button
                onClick={test}
                disabled={busy}
                className="rounded-md border border-felt-line px-3 py-1.5 font-mono text-xs uppercase tracking-wider text-chalk hover:border-brass disabled:opacity-40"
              >
                Send a test
              </button>
              <button
                onClick={turnOff}
                disabled={busy}
                className="font-mono text-xs uppercase tracking-wider text-chalk-dim hover:text-blood disabled:opacity-40"
              >
                Turn off here
              </button>
              {settings && settings.deviceCount > 0 && (
                <span className="font-mono text-xs text-chalk-dim">
                  {settings.deviceCount} device{settings.deviceCount === 1 ? '' : 's'} signed up
                </span>
              )}
            </div>
          </>
        )}

        {note && <p className="font-mono text-xs text-cash">{note}</p>}
        {error && <p className="font-mono text-xs text-blood">{error}</p>}

        {import.meta.env.DEV && (
          <button
            onClick={() => void unregisterServiceWorker().then(() => window.location.reload())}
            className="font-mono text-[0.65rem] uppercase tracking-wider text-chalk-dim underline"
          >
            dev: unregister service worker
          </button>
        )}
      </div>
    </section>
  );
}
